// Vista preparada de un ítem de la carta.
//
// 🔴 POR QUÉ EXISTE: la carta va a tener varias presentaciones (cards con foto,
// filas finas, carta con puntos conductores). Si cada una calculara el precio
// por su cuenta, una sola que olvidara `effectiveItemCardPrice` mostraría el
// precio de EFECTIVO en un local con precio dual — y el cliente pagaría otra
// cosa en el checkout. Acá el dinero se calcula UNA vez y las presentaciones
// solo pintan `cardPrice`: no tienen acceso al precio crudo.
//
// Lo mismo con la ventana de venta: `availableNow` se decide acá, así que
// ninguna presentación puede dejar agregar un ítem fuera de su horario.

import { resolveBadge } from '@/lib/menu/badges'
import { focalPosition } from '@/lib/menu/photoFocal'
import { menuPhotoUrl } from '@/lib/utils/imageTransform'
import { effectiveItemCardPrice, type PricingConfig } from '@/lib/utils/pricing'
import { isItemAvailableNow, availabilityLabel, type ItemAvailability } from '@/lib/menu/availability'
import { localizedItem, type ItemTranslations } from '@/lib/menu/localizeItem'
import { itemHasUnsellableRequiredGroup, type NestedModifierItem } from '@/lib/menu/soldOutGroups'
import { retailCardPrice, retailPickerSoldOut, type RetailPickerFacts } from '@/lib/microsite/retailLine'
import type { OnlineWeightUnit } from '@/lib/retail/onlineWeight'

/** Lo mínimo que el view-model necesita del ítem. */
export type MenuViewItem = NestedModifierItem & {
  id: string
  name: string
  description: string | null
  translations?: ItemTranslations | null
  price: number
  image_url: string | null
  image_focal?: string | null
  /** 86: false = agotado. Se muestra tachado con "Agotado", nunca se oculta. */
  is_available?: boolean | null
  item_badge?: string | null
  badge_ends_at?: string | null
  available_from?: string | null
  available_until?: string | null
  available_days?: number[] | null
} & RetailPickerFacts

export type MenuItemView = {
  id: string
  /** Nombre y descripción ya en el idioma del cliente. */
  name: string
  description: string | null
  /** Precio QUE SE MUESTRA. Ya pasó por effectiveItemCardPrice. */
  cardPrice: number
  badge: ReturnType<typeof resolveBadge>
  /** false ⇒ la presentación lo muestra apagado y no deja agregarlo. */
  availableNow: boolean
  /**
   * 86: agotado HOY. Distinto de `availableNow` (ventana horaria): el agotado
   * lleva badge "Agotado" en vez del reloj. La presentación lo apaga y no deja
   * agregarlo; el server igual rechazaría el checkout con 409 items_unavailable.
   *
   * DOS causas, y la segunda no se veía: `items.is_available=false` (el dueño
   * agotó el plato) o un grupo OBLIGATORIO sin una sola opción disponible (le
   * agotó el último ingrediente). En el segundo caso el plato seguía ofrecido y
   * el cliente se estrellaba al final: `renderableGroups` ocultaba el grupo
   * vacío para no trabar el botón, así que el pedido salía sin ese grupo y lo
   * rechazaba `checkModifierSelections` con 422 al cobrar.
   */
  soldOut: boolean
  /** "Solo viernes", "Desde las 6 PM"… null si se vende siempre. */
  schedLabel: string | null
  /** URL ya redimensionada, o null si el ítem no tiene foto. */
  imageUrl: string | null
  /** `object-position` para el recorte de la foto. */
  focal: string
  /** Súper online (R6): producto por peso → `cardPrice` es POR esta unidad («$3.99/lb»).
   *  Ausente en todo plato. */
  priceUnit?: OnlineWeightUnit
  /** Súper online (R6): con variantes de distinto precio → «desde $X». Ausente en todo plato. */
  priceFrom?: true
}

export function buildMenuItemView(
  item: MenuViewItem,
  opts: { pricingCfg: PricingConfig; tz: string; locale: 'es' | 'en'; photoWidth?: number },
): MenuItemView {
  const availability: ItemAvailability = {
    available_from: item.available_from,
    available_until: item.available_until,
    available_days: item.available_days,
  }
  const display = localizedItem(item, opts.locale)
  // Súper online (R6): solo con las claves de la bandera (weight_unit / variants);
  // un plato da null y todo queda como en main, sin claves nuevas en la vista.
  const retail = retailCardPrice(item, opts.pricingCfg)
  return {
    id: item.id,
    name: display.name,
    description: display.description,
    cardPrice: retail ? retail.cardPrice : effectiveItemCardPrice(item.price, opts.pricingCfg),
    badge: resolveBadge(item.item_badge, item.badge_ends_at),
    availableNow: isItemAvailableNow(availability, opts.tz),
    soldOut: item.is_available === false || itemHasUnsellableRequiredGroup(item) || retailPickerSoldOut(item),
    schedLabel: availabilityLabel(availability, opts.locale),
    imageUrl: item.image_url ? menuPhotoUrl(item.image_url, opts.photoWidth ?? 700) : null,
    focal: focalPosition(item.image_focal),
    ...(retail?.unit ? { priceUnit: retail.unit } : {}),
    ...(retail?.from ? { priceFrom: true as const } : {}),
  }
}

export function buildMenuItemViews(
  items: MenuViewItem[],
  opts: { pricingCfg: PricingConfig; tz: string; locale: 'es' | 'en'; photoWidth?: number },
): MenuItemView[] {
  return items.map((item) => buildMenuItemView(item, opts))
}
